'use client'

import {
  type Options,
  parseAsInteger,
  useQueryStates,
  type UseQueryStatesKeysMap,
  type Values,
} from '@repo/sdk/qs/client'

type TableQueryStatePartial<Map extends UseQueryStatesKeysMap> = Partial<{
  [Key in keyof TableQueryStateValues<Map>]: null | TableQueryStateValues<Map>[Key]
}>
type TableQueryStateUpdate<Map extends UseQueryStatesKeysMap> =
  | ((_oldValues: TableQueryStateValues<Map>) => null | TableQueryStatePartial<Map>)
  | null
  | TableQueryStatePartial<Map>
type TableQueryStateValues<Map extends UseQueryStatesKeysMap> = Values<Map>

const hasQueryStateChanged = <Map extends UseQueryStatesKeysMap>(
  oldValues: TableQueryStateValues<Map>,
  nextValues: TableQueryStatePartial<Map>,
) => {
  for (const key in nextValues) {
    const nextValue = nextValues[key]
    if (nextValue !== undefined && JSON.stringify(oldValues[key]) !== JSON.stringify(nextValue)) {
      return true
    }
  }
  return false
}

export const useTableQueryState = <Map extends UseQueryStatesKeysMap>(keyMap: Map) => {
  const [values, setValues] = useQueryStates({
    ...keyMap,
    page: parseAsInteger.withDefault(1),
  })

  const setValuesAndResetPage = (update: TableQueryStateUpdate<Map>, options?: Options) =>
    setValues(oldValues => {
      const typedOldValues = oldValues as TableQueryStateValues<Map>
      const nextValues = typeof update === 'function' ? update(typedOldValues) : update

      if (nextValues === null) return null
      if (!hasQueryStateChanged(typedOldValues, nextValues)) return {}

      return {
        ...nextValues,
        page: null,
      }
    }, options)

  return {
    setValues,
    setValuesAndResetPage,
    values,
  }
}
